import { Message } from '../types/chat';
import ReactMarkdown from 'react-markdown';
import { useEffect, useRef } from 'react';
import { ArrowDown, Copy, FileText } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import logo from '../../../assets/images/bebrave-logo.svg';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { modelSupportedExtensionsMap, ModelTypes } from '../types/model.types';
import { ImagePreview } from './ImagePreview';
import { useToast } from '../../../hooks/use-toast';
import ExpandableThinkContainer from './ExpandableThinkContainer';
import { useTranslation } from 'react-i18next';
import { Feedback } from './Feedback';

interface ChatMessagesProps {
  messages: Message[];
  isLoading: boolean;
}

export const ChatMessages = ({ messages, isLoading }: ChatMessagesProps) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const scrollToBottomButton = useRef<HTMLButtonElement>(null);
  const { toast } = useToast();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    scrollToBottomButton.current?.style.setProperty('display', 'none');
  };

  useEffect(() => {
    scrollToBottom();
  }, [isLoading]);

  useEffect(() => {
    const handleScroll = () => {
      if (messageContainerRef.current) {
        const { scrollTop, clientHeight, scrollHeight } = messageContainerRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 10) {
          scrollToBottomButton.current?.style.setProperty('display', 'none');
        } else {
          scrollToBottomButton.current?.style.setProperty('display', 'flex');
        }
      }
    };

    const container = messageContainerRef.current;
    container?.addEventListener('scroll', handleScroll);

    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const fallbackCopyToClipboard = (text: string) => {
    const tempTextArea = document.createElement('textarea');
    tempTextArea.value = text;
    document.body.appendChild(tempTextArea);
    tempTextArea.select();

    try {
      document.execCommand('copy');
      console.log('Text copied to clipboard successfully!');
    } catch (err) {
      console.error('Failed to copy using execCommand: ', err);
    } finally {
      document.body.removeChild(tempTextArea);
    }
  };

  const handleCopyToClipboard = (text: string) => {

    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => {
          console.log('Text copied to clipboard successfully!');
        });
      } else {
        fallbackCopyToClipboard(text);
      }
      toast({
        variant: 'success',
        title: t('COPIED'),
        description: t('TEXT_COPY_SUCCESS'),
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_COPY_TEXT'),
        description: t('FAILED_COPY_TO_CLIPBOARD'),
      });
    }
  };

  const agentChatBubbleIcons = [
    {
      icon: <Copy size={16} />,
      onClick: (text: string) => {
        handleCopyToClipboard(text);
      },
      type: 'icon',
      tooltip: t('COPY'),
    },
  ];

  const assistantChatBubbleIcons = [
    {
      icon: <Copy size={16} />,
      onClick: async (text: string) => {
        handleCopyToClipboard(text);
      },
      type: 'icon',
      tooltip: t('COPY'),
    },
  ];

  const messageBubbleUi = (message: Message) => {
    return (
      <div className={`max-w-[90%] flex flex-col mb-1`}>
        <div className={`${message.role == 'user' ? 'bg-[#e9e9e980]' : ''} w-full p-4 rounded-[20px]`}>
          {message.attachments && message.attachments.length > 0 && (
            <div className="grid gap-4 mb-4">
              {message.attachments?.map((file) => {

                if (modelSupportedExtensionsMap[ModelTypes.VISION].image.includes(`.${file.extension}`)) {
                  return (
                    <ImagePreview key={file.fileId} itemId={file.fileId} />
                  );
                }

                return (
                  <div
                    key={file.fileId}
                    className="flex items-center justify-between bg-gray-50 p-2 rounded"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-black truncate max-w-[180px]">{file.fileName}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {message.thinkContent && <ExpandableThinkContainer content={message.thinkContent} isThinking={!message.content} />}
          <ReactMarkdown
            components={{
              p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
              ul: ({ children }) => <ul className="list-disc pl-4 mb-1">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal pl-4 mb-1">{children}</ol>,
              li: ({ children }) => <li className="mb-0.5">{children}</li>,
              code: ({ inline, className, children, ...props }: any) => {
                const match = /language-(\w+)/.exec(className || '');
                if (!inline && match) {
                  return (
                    <SyntaxHighlighter
                      style={dracula}
                      language={match[1]}
                      PreTag="pre"
                      customStyle={{
                        backgroundColor: '#282C34',
                        padding: '1rem',
                        borderRadius: '0.375rem',
                        overflowX: 'auto',
                        fontSize: '0.875rem',
                      }}
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  );
                }
                return (
                  <code
                    className="bg-gray-200 rounded px-1 py-0.5 font-mono text-sm"
                    {...props}
                  >
                    {children}
                  </code>
                );
              },
              pre: ({ children }) => (
                <pre className="w-full  bg-gray-200 rounded p-2 overflow-x-auto mb-1">{children}</pre>
              ),
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-gray-300 pl-4 italic mb-1">
                  {children}
                </blockquote>
              ),
            }}
          >
            {message.content}
          </ReactMarkdown>
        </div>
        <div>
          {message.role == 'assistant' && (
            <div className="flex gap-2 justify-start">
              {agentChatBubbleIcons.map((icon) => (
                <button
                  title={icon.tooltip}
                  key={icon.type}
                  className="h-8 pl-4 hover:bg-muted"
                  onClick={() => icon.onClick(message.content)}
                >
                  {icon.icon}
                </button>
              ))}
              <Feedback message={message} />
            </div>
          )}
          {message.role == 'user' && (
            <div className="flex gap-2 justify-end">
              {assistantChatBubbleIcons.map((icon) => (
                <button
                  title={icon.tooltip}
                  key={icon.type}
                  className="h-8 hover:bg-muted"
                  onClick={() => icon.onClick(message.content)}
                >
                  {icon.icon}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div ref={messageContainerRef} className='w-full overflow-y-auto max-h-[calc(100vh-254px)] flex justify-center'>
      <div
        className="flex flex-col gap-1.5 w-[95%] md:w-[75%] lg:w-[65%] mb-[130px]"
      >
        {messages.map((message) =>
          message.role === 'user' ? (
            <div key={message.id} className="flex justify-end">
              {messageBubbleUi(message)}
            </div>
          ) : (
            <div key={message.id} className="flex justify-start items-start">
              <img
                className="w-7 h-7 mt-[20px] border-[1px] p-1 rounded-full outline-gray-200 bg-primary-50"
                src={logo}
                alt="bebrave logo"
              />
              {messageBubbleUi(message)}
            </div>
          )
        )}
        {isLoading && (
          <div className="flex justify-start">
            <div className="max-w-[80%] rounded-lg bg-gray-100 border border-gray-200 p-3">
              <div className="flex gap-2">
                <div className="h-2 w-2 animate-bounce rounded-full bg-gray-400" />
                <div
                  className="h-2 w-2 animate-bounce rounded-full bg-gray-400"
                  style={{ animationDelay: '0.2s' }}
                />
                <div
                  className="h-2 w-2 animate-bounce rounded-full bg-gray-400"
                  style={{ animationDelay: '0.4s' }}
                />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="flex justify-center absolute w-[85%] md:w-[50%] lg:w-[50%] bottom-44 sm:bottom-[154px] md:bottom-[162px]">
        <Button
          ref={scrollToBottomButton}
          variant="outline"
          aria-label="Scroll to bottom"
          className="h-10 rounded-full border w-10"
          onClick={scrollToBottom}
        >
          <ArrowDown size={16} />
        </Button>
      </div>
    </div>
  );
};
